/**
 * CS: Announcement Bar Countdown Timer.
 *
 * Plain IIFE, no per-element listeners: Horizon's morph engine drops those on
 * re-render, so timers are (re)initialised by scan + MutationObserver instead.
 *
 * Resolves the merchant's local end time in a chosen US timezone to one
 * absolute instant, so every shopper counts down to the same moment.
 *
 * The discount code chip in this section is display only and needs no JS.
 */
(function () {
  if (window.__csCountdownBarInit) return;
  window.__csCountdownBarInit = true;

  /* [standard offset, daylight offset]. Equal pair = zone has no DST. */
  var TZ = {
    'America/New_York': ['-05:00', '-04:00'],
    'America/Chicago': ['-06:00', '-05:00'],
    'America/Denver': ['-07:00', '-06:00'],
    'America/Phoenix': ['-07:00', '-07:00'],
    'America/Los_Angeles': ['-08:00', '-07:00'],
  };

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  /**
   * Which UTC offset is live in `tz` on the given calendar date.
   * Builds noon at the daylight offset and reads the hour back in the target
   * zone: if it still reads 12, daylight time is in force.
   * @returns {string|null} an ISO offset, or null when the zone is unknown.
   */
  function offsetFor(tz, y, m, d) {
    var pair = TZ[tz];
    if (!pair) return null;
    if (pair[0] === pair[1]) return pair[0];
    try {
      var probe = new Date(y + '-' + m + '-' + d + 'T12:00:00' + pair[1]);
      if (isNaN(probe.getTime())) return pair[0];
      var hour = probe.toLocaleString('en-US', { timeZone: tz, hour: 'numeric', hour12: false });
      return parseInt(hour, 10) === 12 ? pair[1] : pair[0];
    } catch (e) {
      return pair[0];
    }
  }

  /** @returns {number} epoch ms for the configured end, or NaN. */
  function targetMs(el) {
    var date = (el.getAttribute('data-cs-end-date') || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return NaN;

    var time = (el.getAttribute('data-cs-end-time') || '').trim();
    if (!/^\d{1,2}:\d{1,2}$/.test(time)) time = '23:59';
    var hm = time.split(':');
    var hh = pad(Math.min(23, Math.max(0, parseInt(hm[0], 10) || 0)));
    var mm = pad(Math.min(59, Math.max(0, parseInt(hm[1], 10) || 0)));

    var parts = date.split('-');
    var off = offsetFor(el.getAttribute('data-cs-tz') || '', parts[0], parts[1], parts[2]);

    /* No offset means an unknown zone: fall back to the shopper's own clock. */
    return new Date(date + 'T' + hh + ':' + mm + ':00' + (off || '')).getTime();
  }

  function initTimer(el) {
    if (!el || el.__csCountdownInit) return;
    el.__csCountdownInit = true;

    var root = el.closest('.cs-countdown-bar');
    var onEnd = el.getAttribute('data-cs-on-end') || 'hide';
    var showDays = el.getAttribute('data-cs-show-days') || 'auto';

    var daysSeg = el.querySelector('[data-cs-days-seg]');
    var daysSep = el.querySelector('[data-cs-days-sep]');
    var dEl = el.querySelector('[data-cs-days]');
    var hEl = el.querySelector('[data-cs-hours]');
    var mEl = el.querySelector('[data-cs-minutes]');
    var sEl = el.querySelector('[data-cs-seconds]');
    var srEl = root && root.querySelector('[data-cs-sr]');
    var expiredEl = root && root.querySelector('[data-cs-expired]');

    var target = targetMs(el);
    var lastSrKey = '';
    var timerId = null;

    function stop() {
      if (timerId) clearInterval(timerId);
      timerId = null;
    }

    function expire() {
      stop();
      if (onEnd === 'text' && expiredEl) {
        el.hidden = true;
        expiredEl.hidden = false;
        if (srEl) srEl.textContent = expiredEl.textContent;
      } else if (root) {
        root.hidden = true;
      }
    }

    function tick() {
      /* Morph can detach the node; do not leak an interval behind it. */
      if (!el.isConnected) return stop();
      if (isNaN(target)) return expire();

      var total = Math.floor((target - Date.now()) / 1000);
      if (total <= 0) return expire();

      var d = Math.floor(total / 86400);
      var useDays = showDays === 'always' || (showDays === 'auto' && d > 0);
      var h = useDays ? Math.floor((total % 86400) / 3600) : Math.floor(total / 3600);
      var m = Math.floor((total % 3600) / 60);
      var s = total % 60;

      if (daysSeg) daysSeg.hidden = !useDays;
      if (daysSep) daysSep.hidden = !useDays;
      if (dEl && useDays) dEl.textContent = pad(d);
      if (hEl) hEl.textContent = pad(h);
      if (mEl) mEl.textContent = pad(m);
      if (sEl) sEl.textContent = pad(s);

      /* Screen readers get a coarse summary that changes at most once a
         minute, never the per-second digits. */
      if (srEl) {
        var key, msg;
        if (total < 3600) {
          key = 'm' + m;
          msg = m + (m === 1 ? ' minute left' : ' minutes left');
        } else if (useDays) {
          key = 'd' + d + 'h' + h;
          msg = d + (d === 1 ? ' day ' : ' days ') + h + (h === 1 ? ' hour left' : ' hours left');
        } else {
          key = 'h' + h;
          msg = h + (h === 1 ? ' hour left' : ' hours left');
        }
        if (key !== lastSrKey) {
          lastSrKey = key;
          srEl.textContent = msg;
        }
      }
    }

    tick();
    timerId = setInterval(tick, 1000);

    /* Background tabs throttle setInterval. Re-derive and repaint on return so
       the digits are never stale, including across a DST change. */
    var onVisible = function () {
      /* Unbind once morph has replaced our node, so listeners cannot pile up. */
      if (!el.isConnected) return document.removeEventListener('visibilitychange', onVisible);
      if (document.hidden) return;
      target = targetMs(el);
      tick();
    };
    document.addEventListener('visibilitychange', onVisible);
  }

  function scan(scope) {
    var nodes = (scope || document).querySelectorAll('[data-cs-countdown]');
    for (var i = 0; i < nodes.length; i++) initTimer(nodes[i]);
  }

  /* ---------- Boot ---------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  } else {
    scan();
  }

  /* Theme editor re-renders the section on every setting change. */
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  /* Horizon morphs the header group on cart updates, replacing our nodes. */
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-cs-countdown]')) initTimer(node);
          else if (node.querySelectorAll) scan(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
